// The deck as a whole (SC, Features 4, 26 Oct 2026, David: "Slides that design like PowerPoint", group 5): sections,
// hidden slides and Morph's matching. Light on purpose: the board, the server and the agents' tools share it.
//
// A section is a named run of slides, as PowerPoint's are: it starts at the slide that carries its name (`section` on
// the slide entry) and runs to the next slide that carries one. Slides before the first named one are in no section
// (PowerPoint calls that run "Default Section"). Moving slides keeps every slide that does not move in the section it
// was in, and a section moved whole stays one; the names are put back on the slides that start each run afterwards,
// so a merge of two writers is still slide by slide.
//
// A hidden slide (`hidden: true`) is skipped when presenting, on a shared link and in the PDF, drawn faded in the strip
// and the sorter, and kept in the PowerPoint file as PowerPoint's own hidden slide (show="0").

import type { Entry, Op } from "./types";

export const MAX_SECTION_NAME = 60;
/** What PowerPoint calls the slides before the first section, shown only when a deck has sections. */
export const DEFAULT_SECTION = "Default section";
/** A new section's name until it is given one, as PowerPoint names it. */
export const UNTITLED_SECTION = "Untitled section";

/** A section's name as kept: one plain line, or null for none. */
export function cleanSectionName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw
    .replace(/[\u0000-\u001f\u007f<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_SECTION_NAME);
  return s || null;
}

type SlideLike = { id: string; data: Record<string, unknown> };

export const sectionOf = (e: SlideLike): string | null => cleanSectionName(e.data.section);
export const isHidden = (e: SlideLike | { hidden?: boolean }): boolean => ("data" in e ? e.data.hidden === true : e.hidden === true);

export interface SectionRun {
  /** its name; null for the slides before the first section */
  name: string | null;
  /** the slide that starts it (carries its name); null for the slides before the first section */
  head: string | null;
  /** its slides, in order */
  ids: string[];
  /** its place among the deck's runs, from 0 */
  index: number;
}

/** A deck's slides (in order) as runs of sections. A deck with no sections is one run with no name. */
export function sectionRuns(slides: SlideLike[]): SectionRun[] {
  const out: SectionRun[] = [];
  for (const e of slides) {
    const name = sectionOf(e);
    if (name !== null || !out.length) out.push({ name, head: name !== null ? e.id : null, ids: [], index: out.length });
    out[out.length - 1].ids.push(e.id);
  }
  return out;
}

export const hasSections = (slides: SlideLike[]): boolean => slides.some((e) => sectionOf(e) !== null);

/** The slides a talk shows: every one not hidden, in order. */
export function shownSlides<E extends SlideLike>(slides: E[]): E[] {
  return slides.filter((e) => !isHidden(e));
}

/** Who each slide belongs to: its run's head (or "" for the slides before the first section). */
function memberships(slides: SlideLike[]): Map<string, { key: string; name: string | null }> {
  const out = new Map<string, { key: string; name: string | null }>();
  for (const r of sectionRuns(slides)) for (const id of r.ids) out.set(id, { key: r.head ?? "", name: r.name });
  return out;
}

/**
 * The names each slide should carry for these memberships, in this order: a run starts where the membership changes.
 * A run of no section after a named one takes the name PowerPoint gives it ("Default section"), since a deck's slides
 * after its first section are always in one.
 */
function headsFor(order: string[], member: Map<string, { key: string; name: string | null }>): Map<string, string | null> {
  const out = new Map<string, string | null>();
  let prev: string | null = null;
  order.forEach((id, i) => {
    const m = member.get(id) ?? { key: "", name: null };
    const starts = i === 0 || m.key !== prev;
    out.set(id, starts ? (m.name ?? (i === 0 ? null : DEFAULT_SECTION)) : null);
    prev = m.key;
  });
  return out;
}

/** The section names to change, slide by slide, as entry puts, for a new order and memberships. */
function namePuts(slides: Entry[], order: string[], member: Map<string, { key: string; name: string | null }>, orders?: Map<string, number>): Op[] {
  const heads = headsFor(order, member);
  const ops: Op[] = [];
  for (const e of slides) {
    const want = heads.get(e.id) ?? null;
    const was = sectionOf(e);
    const order2 = orders?.get(e.id);
    if (want === was && order2 === undefined) continue;
    const data = { ...e.data };
    // a name taken away is sent as null: a slide's put keeps what it does not say (shared/slides.ts cleanSlide)
    data.section = want ?? null;
    ops.push({ op: "entry.put", entry: { ...e, ...(order2 !== undefined ? { order: order2 } : {}), data } });
  }
  return ops;
}

/**
 * Slides moved together to a place among the slides that stay (`to`, as deck.ts moveSlides counts it), with their
 * sections kept: every slide that stays keeps the section it was in; a section moved whole stays one, wherever it
 * lands; slides moved without the rest of their section join the section they land in. Returns the order for each
 * slide moved (as the caller computed them) merged with any name that moves, as one list of entry puts.
 */
export function movedWithSections(slides: Entry[], ids: string[], to: number, orders: Map<string, number>): Op[] {
  const moving = slides.filter((s) => ids.includes(s.id));
  const rest = slides.filter((s) => !ids.includes(s.id));
  const at = Math.max(0, Math.min(rest.length, to));
  const order = [...rest.slice(0, at), ...moving, ...rest.slice(at)].map((s) => s.id);
  if (!hasSections(slides)) return slides.filter((s) => orders.has(s.id)).map((s): Op => ({ op: "entry.put", entry: { ...s, order: orders.get(s.id)! } }));
  const member = memberships(slides);
  const runs = sectionRuns(slides);
  // a section moved whole keeps its name; any other slide moved joins where it lands (the run of the slide before it)
  const whole = new Set(runs.filter((r) => r.head && r.ids.every((id) => ids.includes(id))).map((r) => r.head!));
  const landed = at > 0 ? member.get(rest[at - 1].id)! : rest[0] ? member.get(rest[0].id)! : { key: "", name: null };
  for (const s of moving) {
    const m = member.get(s.id)!;
    if (!whole.has(m.key)) member.set(s.id, landed);
  }
  return namePuts(slides, order, member, orders);
}

/** The names to change once slides are gone: each run that loses its first slide starts at the next one left. */
export function removedWithSections(slides: Entry[], gone: string[]): Op[] {
  if (!hasSections(slides)) return [];
  const member = memberships(slides);
  const left = slides.filter((s) => !gone.includes(s.id));
  return namePuts(left, left.map((s) => s.id), member);
}

export type SectionMove = "up" | "down";

/** A whole section moved one section up or down: the ids moving and where among the rest they go (moveSlides' `to`). */
export function sectionMove(slides: SlideLike[], head: string | null, dir: SectionMove): { ids: string[]; to: number } | null {
  const runs = sectionRuns(slides);
  const i = runs.findIndex((r) => r.head === head);
  if (i < 0) return null;
  const j = dir === "up" ? i - 1 : i + 1;
  if (j < 0 || j >= runs.length) return null;
  const ids = runs[i].ids;
  const rest = slides.filter((s) => !ids.includes(s.id)).map((s) => s.id);
  const to = dir === "up" ? rest.indexOf(runs[j].ids[0]) : rest.indexOf(runs[j].ids[runs[j].ids.length - 1]) + 1;
  return { ids, to };
}

/**
 * Slides put in a section of their own (an agent's "put slides 5 to 8 in a section called Results", or Add section on
 * slides picked): they must sit together. The first gets the name; the rest join it; the slide after them starts again
 * the section it was in (named as PowerPoint names a run: its own name, else "Untitled section"). Returns the puts, or
 * words saying why not.
 */
export function sectionFor(slides: Entry[], ids: string[], name: string): Op[] | string {
  const clean = cleanSectionName(name);
  if (!clean) return "A section needs a name.";
  const at = slides.map((s, i) => (ids.includes(s.id) ? i : -1)).filter((i) => i >= 0);
  if (!at.length) return "None of those slides are in this deck.";
  const a = at[0];
  const b = at[at.length - 1];
  if (b - a + 1 !== at.length) return "The slides of a section sit together: move them next to each other first, or name a run of slides (from one number to another).";
  const member = memberships(slides);
  const after = slides[b + 1];
  // the slides after the run go on in the section they were in (one with no name before takes PowerPoint's)
  const afterWas = after ? member.get(after.id)! : null;
  const key = `new:${slides[a].id}`;
  for (let i = a; i <= b; i++) member.set(slides[i].id, { key, name: clean });
  if (after && afterWas) {
    const tail = { key: `${afterWas.key}:after`, name: afterWas.name ?? UNTITLED_SECTION };
    for (let i = b + 1; i < slides.length && member.get(slides[i].id)!.key === afterWas.key; i++) member.set(slides[i].id, tail);
  }
  return namePuts(slides, slides.map((s) => s.id), member);
}

/** A section's name changed on the slide that starts it. */
export function renamedSection(slides: Entry[], head: string, name: string): Op[] | string {
  const clean = cleanSectionName(name);
  if (!clean) return "A section needs a name.";
  const e = slides.find((s) => s.id === head);
  if (!e || sectionOf(e) === null) return "That slide does not start a section.";
  if (sectionOf(e) === clean) return [];
  return [{ op: "entry.put", entry: { ...e, data: { ...e.data, section: clean } } }];
}

/** A section taken away, its slides kept: they join the section before (as PowerPoint's Remove Section does). */
export function withoutSection(slides: Entry[], head: string): Op[] {
  const e = slides.find((s) => s.id === head);
  if (!e || sectionOf(e) === null) return [];
  return [{ op: "entry.put", entry: { ...e, data: { ...e.data, section: null } } }];
}

/** Every section taken away (PowerPoint's Remove All Sections), the slides kept. */
export function withoutSections(slides: Entry[]): Op[] {
  return slides.filter((e) => sectionOf(e) !== null).flatMap((e) => withoutSection(slides, e.id));
}
